/**
 * RESILIENT LISTENERS - every session listener's error policy, and the
 * app-level notices it raises: a failure, an outage and its end, a revoked
 * session, a sign-in the server no longer honours.
 *
 * Apart from multiplayer.ts, which re-exports the handler setters, because it
 * is one concern with its own state (the outage clock) and every listener
 * there shares it.
 *
 * A Firestore onSnapshot listener with NO error callback dies silently and
 * permanently on its first error (observed live: a rules denial racing the
 * membership doc on a fresh session killed the permissions listener for the
 * session's lifetime - see gotchas.md). Every session listener below goes
 * through this wrapper: errors are logged with a label and the subscription
 * is re-established with exponential backoff (1s → 30s cap).
 *
 * IT NO LONGER GIVES UP (v8.199.17). Retrying used to stop after six
 * attempts, on the reasoning that a minute of failure was a fact about
 * the world - a session deleted under an open tab. That case is the
 * DENIED path below now (v8.97.0 gave it its own persistence test and
 * its own exit), which left the give-up punishing only genuine network
 * outages: an SSE drop through the relay, a machine asleep past the
 * budget. One listener died FOREVER while its siblings recovered, under
 * a toast blaming a session that still existed. Plain errors now retry
 * indefinitely at the 30s cap, and the app is told the truth instead -
 * one lost notice per outage, one restored notice when delivery resumes.
 */
import { backend } from './backend/app';
import type { Unsubscribe } from './backend/types';
import { diag, errorSummary } from './diagnostics/log';

type ListenerFailureHandler = (label: string, err: unknown) => void;
let onListenerFailure: ListenerFailureHandler | null = null;

/** Register the app's handler for a refused WRITE (reportWriteFailure). */
export function setListenerFailureHandler(handler: ListenerFailureHandler | null): void {
  onListenerFailure = handler;
}

type ConnectionNotice = 'lost' | 'restored';
type ConnectionNoticeHandler = (state: ConnectionNotice) => void;
let onConnectionNotice: ConnectionNoticeHandler | null = null;
/**
 * Every listener instance currently failing. One outage degrades many
 * listeners at once; the SIZE transitions of this set - not the
 * individual errors - are what the user should hear about, exactly once
 * per outage. Keyed by instance token, not label: two listeners may
 * share a label and each holds its own place in the outage.
 */
const degraded = new Set<symbol>();

/**
 * How long an outage must LAST before anyone is told (v8.310.5).
 *
 * The PocketBase SDK rejects every pending subscription on the first
 * realtime connection error, with no retry of its own - so one hiccup at
 * join failed every listener at once, "Connection to the server lost"
 * went up, and "Reconnected" followed it a second later when the 1s
 * backoff healed them all. True, and alarming, and the report from the
 * table was "it says it failed to connect". A blip that heals inside the
 * grace says nothing; an outage that outlasts it is announced once, and
 * its recovery once.
 */
const OUTAGE_GRACE_MS = 3000;
let graceTimer: ReturnType<typeof setTimeout> | null = null;
let outageAnnounced = false;

function outageBegan(err: unknown): void {
  if (graceTimer) return;
  graceTimer = setTimeout(() => {
    graceTimer = null;
    if (degraded.size === 0) return;
    outageAnnounced = true;
    diag.warn('sync', `connection lost for over ${OUTAGE_GRACE_MS}ms`, errorSummary(err));
    onConnectionNotice?.('lost');
  }, OUTAGE_GRACE_MS);
}

function outageEnded(): void {
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  if (!outageAnnounced) return;
  outageAnnounced = false;
  diag.info('sync', 'connection restored');
  onConnectionNotice?.('restored');
}

/** Register the app's handler for the connection being lost / restored. */
export function setConnectionNoticeHandler(handler: ConnectionNoticeHandler | null): void {
  onConnectionNotice = handler;
  if (!handler) {
    degraded.clear();
    if (graceTimer) clearTimeout(graceTimer);
    graceTimer = null;
    outageAnnounced = false;
  }
}

type SessionRevokedHandler = (label: string) => void;
let onSessionRevoked: SessionRevokedHandler | null = null;

type AuthExpiredHandler = () => void;
let onAuthExpired: AuthExpiredHandler | null = null;
let authExpiryReported = false;

/**
 * Register the app's handler for "the server no longer honours this sign-in".
 *
 * Neither a refusal nor an outage: a rotated or expired token, which only a
 * fresh sign-in can mend. Reported ONCE - every listener sees it at the same
 * moment, and one toast is the truth while ten are a panic.
 */
export function setAuthExpiredHandler(handler: AuthExpiredHandler | null): void {
  onAuthExpired = handler;
  authExpiryReported = false;
}

function noteAuthExpired(label: string, err: unknown): void {
  if (authExpiryReported) return;
  authExpiryReported = true;
  diag.error('auth', `${label}: sign-in no longer honoured`, errorSummary(err));
  onAuthExpired?.();
}

/**
 * Register the app's handler for "you are not in this session any more".
 *
 * Separate from the failure handler because it means something different and
 * needs a different response: not "something went wrong, maybe reload" but
 * "this is over, go back to the lobby".
 */
export function setSessionRevokedHandler(handler: SessionRevokedHandler | null): void {
  onSessionRevoked = handler;
}

/**
 * Report a WRITE that was refused, through the same channel.
 *
 * A listener that dies is loud already. A write that is refused was not:
 * it logged to a console and the next snapshot quietly replaced the work
 * with the server's older copy, so a build that never saved looked like
 * a build that had been deleted. Anything that writes something a person
 * made should call this when it fails.
 */
export function reportWriteFailure(message: string, err: unknown): void {
  diag.error('sync', `write refused: ${message}`, errorSummary(err));
  if (backend.errorKind(err) === 'unauthenticated') {
    noteAuthExpired(message, err);
    return;
  }
  onListenerFailure?.(message, err);
}

/**
 * How long a refusal has to PERSIST before it means "you are not in this
 * session any more", and how often to re-ask while deciding.
 *
 * A denial is not self-evidently permanent, which was the tempting mistake:
 * membership writes make other clients' reads bounce briefly, so a survivor of
 * a kick sees the same `permission-denied` the kicked player does - and
 * ejecting on the first one throws innocent people out of a session nobody
 * touched, which is v8.25.0 by another route. Measured: treating one denial as
 * final failed the survivor guard two runs in three.
 *
 * What distinguishes them is persistence, not the error. Three quick re-asks
 * over ~2.5s outlast the transient case comfortably and still return a kicked
 * player to the lobby in a fraction of the ~61s the general backoff took.
 */
const DENIED_RETRY_MS = [300, 700, 1500];

/**
 * How long a listener must STAY healthy before its failures are forgiven
 * (2026-10-03).
 *
 * Not every delivery proves the server answered. Re-subscribing, Firestore
 * hands over what its cache holds at once and the server's verdict follows
 * a round trip later - so a refusal reset its own count on every re-ask: a
 * kicked player re-asked every 300 ms for good and never reached the lobby,
 * and a failing listener re-asked every second instead of backing off. A
 * delivery starts this clock; an error before it runs out stops it.
 */
const HEALTHY_SETTLE_MS = 5000;

/** Stop a subscription that may already have terminated itself. */
function dropSubscription(unsub: Unsubscribe | null): void {
  try {
    unsub?.();
  } catch {
    /* listener already terminated */
  }
}

/** A listener torn down mid-outage must not hold the outage open - but its departure is not a recovery either. */
function leaveOutage(token: symbol): void {
  degraded.delete(token);
  if (degraded.size === 0 && graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
}

/** Start a subscription, handing it the listener's error and health callbacks. */
type StartListener = (onError: (err: unknown) => void, onHealthy: () => void) => Unsubscribe;

/**
 * One resilient subscription: re-established after an error, with a
 * budget per kind of failure. The callbacks are fields, not methods, so
 * every restart hands the backend the SAME two functions.
 */
class ResilientListener {
  private inner: Unsubscribe | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private forgiveTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private attempt = 0;
  private deniedAttempt = 0;
  private readonly token: symbol;
  private readonly label: string;
  private readonly start: StartListener;

  constructor(label: string, start: StartListener) {
    this.label = label;
    this.start = start;
    this.token = Symbol(label);
  }

  open(): Unsubscribe {
    this.inner = this.start(this.onError, this.onHealthy);
    return () => this.close();
  }

  // A delivery that LASTS means the listener is healthy again, so both
  // budgets start over. Without this a session that survived three scattered
  // denials would eject its user on the fourth, hours later. Only one that
  // lasts - see HEALTHY_SETTLE_MS.
  private readonly onHealthy = () => {
    if (degraded.delete(this.token) && degraded.size === 0) outageEnded();
    if (this.forgiveTimer || (this.attempt === 0 && this.deniedAttempt === 0)) return;
    this.forgiveTimer = setTimeout(() => {
      this.forgiveTimer = null;
      this.attempt = 0;
      this.deniedAttempt = 0;
    }, HEALTHY_SETTLE_MS);
  };

  private unforgiven(): void {
    if (this.forgiveTimer) clearTimeout(this.forgiveTimer);
    this.forgiveTimer = null;
  }

  // A REFUSAL THAT PERSISTS IS AN ANSWER.
  //
  // When the GM kicks a player, `deletePlayer` removes the player document
  // and then revokes membership. From the second write on, every listener
  // the kicked client owns is refused for good - but a survivor's listeners
  // can bounce off the same write for a moment, so only a denial that
  // outlasts DENIED_RETRY_MS tells them apart. Before this, the kicked
  // player sat in a frozen session for ~61s and was told "lost contact",
  // and whether they were told at all depended on a race (v8.97.0).
  //
  // Only the adapter can tell a refusal from a dropped connection, which is
  // why `errorKind` is on the port.
  private readonly onError = (err: unknown) => {
    if (this.stopped) return;
    this.unforgiven();
    dropSubscription(this.inner);
    const kind = backend.errorKind(err);
    if (kind === 'unauthenticated') return this.signInGone(err);
    if (kind === 'missing') return this.collectionMissing(err);
    if (kind === 'denied') return this.refused(err);
    this.outage(err);
  };

  /**
   * Not a refusal to re-ask and not an outage to wait out: the sign-in
   * itself is gone, and only a fresh one brings the listeners back.
   */
  private signInGone(err: unknown): void {
    this.stopped = true;
    noteAuthExpired(this.label, err);
  }

  /**
   * THE COLLECTION IS NOT THERE (v8.361.1). A PocketBase volume that a
   * migration never reached answers 404 for ever: no retry mends it,
   * and before this the listener backed off to 30s and re-asked for
   * the rest of the session while the outage clock - started by this
   * very failure - put "reconnecting" over a table that was fine.
   * Stop, say so once through the failure channel, and leave the
   * outage clock alone. The fix is on the server (a migration).
   */
  private collectionMissing(err: unknown): void {
    const { label } = this;
    this.stopped = true;
    console.error(`[sync] ${label} stopped - the collection is missing on this backend; a migration has not run`);
    diag.error(
      'sync',
      `${label} stopped - collection missing on this backend (run the pending migration)`,
      errorSummary(err),
    );
    onListenerFailure?.(`${label}: the collection is missing on this server - an update has not been applied`, err);
  }

  private refused(err: unknown): void {
    const { label } = this;
    if (this.deniedAttempt >= DENIED_RETRY_MS.length) {
      this.stopped = true;
      console.error(`[sync] ${label} refused repeatedly - this client is no longer a member of the session`);
      diag.error('sync', `${label} refused ${DENIED_RETRY_MS.length + 1} times - session revoked`, errorSummary(err));
      onSessionRevoked?.(label);
      return;
    }
    const wait = DENIED_RETRY_MS[this.deniedAttempt];
    this.deniedAttempt++;
    diag.warn('sync', `${label} refused - re-asking in ${wait}ms`, errorSummary(err));
    this.restartIn(wait);
  }

  private outage(err: unknown): void {
    const { label } = this;
    console.error(`[firestore] ${label} listener error - resubscribing`, err);
    // The FIRST listener into an outage starts the clock; the rest join in
    // silence. Restoration is announced by whoever leaves last - and only
    // if the outage was ever announced.
    if (degraded.size === 0) outageBegan(err);
    degraded.add(this.token);
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(this.attempt, 10));
    this.attempt++;
    diag.warn('sync', `${label} listener failed - retry in ${delay}ms`, errorSummary(err));
    this.restartIn(delay);
  }

  private restartIn(ms: number): void {
    this.retryTimer = setTimeout(() => {
      if (!this.stopped) this.inner = this.start(this.onError, this.onHealthy);
    }, ms);
  }

  private close(): void {
    this.stopped = true;
    leaveOutage(this.token);
    this.unforgiven();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    dropSubscription(this.inner);
  }
}

/** Wrap a session listener so it survives errors - see the header of this file. */
export function resilientListener(label: string, start: StartListener): Unsubscribe {
  return new ResilientListener(label, start).open();
}
